import JobSearchFiltersForm from '@/modules/JobSearchFilters/components/JobSearchFiltersForm/JobSearchFiltersForm';
import { ILocation, ISchedulerLocation } from '@/services/types';
import {
  DateSearch,
  IRootSpecialityV2,
  ISpeciality,
  ISubSpeciality,
  JobGrade,
  JobSearchGrade,
  JobSearchGrades,
  JobSearchRootSpecialitiesV2,
  JobSearchRootSpeciality,
  JobSearchSpecialities,
  JobSearchSpeciality,
  JobSearchSubSpecialities,
  JobSearchSubSpeciality,
  JobSearchWebCriteria,
  MinPayRateUnit,
  WorkType,
} from '@/types/jobs';
import { isValidDate } from '@/utils/dateHelper';
import { generateJobListingsUrlObject } from '@/utils/jobListingHelper';
import { generateLocationNameFromLocationString } from '@/utils/jobLocationsHelper';
import {
  DEFAULT_MIN_PAY_RATE_UNIT,
  DEFAULT_RADIUS,
  getGradeOptions,
  getRootSpecialityOptions,
  getSpecialityOptions,
  getSubSpecialityOptions,
} from '@/utils/jobSearchBoxOptions';
import { setGradeCookie, setLocationCookie, setSpecialityCookie } from '@/utils/jobSearchCookies';
import { useAnalytics } from '@/utils/useAnalytics';
import {
  getJobGrades,
  getRootSpecialtiesV2,
  getSpecialities,
  getSubSpecialitiesV2,
} from '@/utils/useRequest';
import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';

export type JobSearchFiltersProps = {
  query?: JobSearchWebCriteria;
  showSubSpecialtyFormFilter: boolean;
};

type GradeFilter = JobSearchGrade | undefined;
type SpecialityFilter = JobSearchSpeciality | undefined;
type RootSpecialityFilter = JobSearchRootSpeciality | undefined;
type SubSpecialityFilter = JobSearchSubSpeciality | undefined;
type KeywordsFilter = string | undefined;
type WorkTypeFilter = WorkType | undefined;
type DateFilter = DateSearch;
type RadiusFilter = string | undefined;
type LocationCoordinateFilter = ILocation | ISchedulerLocation | null;
type MinPayRateFilter = string | null;
type MinPayRateUnitFilter = MinPayRateUnit;

const JobSearchFilters = ({ query, showSubSpecialtyFormFilter }: JobSearchFiltersProps) => {
  const router = useRouter();
  const track = useAnalytics();

  // grade and speciality lists
  const [grades, setGrades] = useState<JobSearchGrades | null>([
    { key: 'Loading', value: 'Loading' },
  ]);
  const [specialities, setSpecialities] = useState<JobSearchSpecialities>({});
  const [rootSpecialities, setRootSpecialities] = useState<JobSearchRootSpecialitiesV2>({});
  const [subSpecialities, setSubSpecialities] = useState<JobSearchSubSpecialities>({});

  // form state
  const [date, setDate] = useState<DateFilter>({
    startDate: query?.startDate ?? null,
    endDate: query?.endDate ?? null,
  });
  const [grade, setGrade] = useState<GradeFilter>(query?.grade);

  // TODO: update the below speciality states when ready
  const [speciality, setSpeciality] = useState<SpecialityFilter>(query?.speciality);
  const [rootSpeciality, setRootSpeciality] = useState<RootSpecialityFilter>(query?.rootSpeciality);
  const [subspeciality, setSubspeciality] = useState<SubSpecialityFilter>(query?.subspeciality);

  const [keywords, setKeywords] = useState<KeywordsFilter>(query?.keywords);
  const [workType, setWorkType] = useState<WorkTypeFilter>(query?.workType);
  const [radius, setRadius] = useState<RadiusFilter>(query?.radius);
  const [locationCoordinate, setLocationCoordinate] = useState<LocationCoordinateFilter>({
    name: query?.locationName
      ? query?.locationName
      : generateLocationNameFromLocationString(query?.location),
    position: query?.lat && query?.lon ? { lat: +query.lat, lon: +query.lon } : undefined,
    searchLocations: query?.location ? query.location?.split(';') : undefined,
  });
  const [minPayRate, setMinPayRate] = useState<MinPayRateFilter>(query?.minPayRate ?? null);
  const [minPayRateUnit, setMinPayRateUnit] = useState<MinPayRateUnitFilter>(
    query?.minPayRateUnit ?? DEFAULT_MIN_PAY_RATE_UNIT,
  );
  // end of form state

  const isEndDateDisabled = workType === 'Permanent';
  const isRadiusDisabled = !(
    (locationCoordinate as ILocation)?.position?.lon &&
    (locationCoordinate as ILocation)?.position?.lat
  );

  const getSpecialityByGrade = async (
    newGrade: JobSearchGrade | undefined,
  ): Promise<JobSearchSpeciality | undefined> => {
    if (!newGrade || newGrade === 'none') {
      return undefined;
    }

    const filteredGrade = newGrade as JobGrade;
    let specialitiesPerGrade: Array<ISpeciality>;

    // fetch allowed specialty values per selected grade if not fetched yet
    if (specialities[filteredGrade]) {
      specialitiesPerGrade = specialities[filteredGrade];
    } else {
      const result = await getSpecialities(filteredGrade);
      if ('error' in result) {
        // the error get reported by makeRequestAndNoticeErrors, and we don't show any notification to the user
        return undefined;
      } else {
        setSpecialities({
          ...specialities,
          [filteredGrade]: result.response.data,
        });
        specialitiesPerGrade = result.response.data;
      }
    }

    // reset selected speciality if it is not found in the allowed specialty values
    if (specialitiesPerGrade && specialitiesPerGrade.length > 0) {
      const specialitiesSet: Set<JobSearchSpeciality | undefined> = new Set(
        specialitiesPerGrade.map(i => i.value),
      );
      return specialitiesSet.has(speciality) ? speciality : undefined;
    }

    return undefined;
  };

  const getRootSpecialityByGrade = async (
    newGrade: JobSearchGrade | undefined,
  ): Promise<JobSearchRootSpeciality | undefined> => {
    if (!newGrade || newGrade === 'none') {
      return undefined;
    }

    const filteredGrade = newGrade as JobGrade;
    let rootSpecialitiesPerGrade: Array<IRootSpecialityV2>;

    // fetch allowed specialty values per selected grade if not fetched yet
    if (rootSpecialities[filteredGrade]) {
      rootSpecialitiesPerGrade = rootSpecialities[filteredGrade];
    } else {
      const result = await getRootSpecialtiesV2(filteredGrade);
      if ('error' in result) {
        // the error get reported by makeRequestAndNoticeErrors, and we don't show any notification to the user
        return undefined;
      } else {
        setRootSpecialities({
          ...rootSpecialities,
          [filteredGrade]: result.response.data,
        });
        rootSpecialitiesPerGrade = result.response.data;
      }
    }

    // reset selected speciality if it is not found in the allowed specialty values
    if (rootSpecialitiesPerGrade && rootSpecialitiesPerGrade.length > 0) {
      const rootSpecialitiesSet: Set<JobSearchRootSpeciality | undefined> = new Set(
        rootSpecialitiesPerGrade.map(i => i.value),
      );
      return rootSpecialitiesSet.has(rootSpeciality) ? rootSpeciality : undefined;
    }

    return undefined;
  };

  const getSubSpecialityByRootSpeciality = async (
    newGrade: JobSearchGrade | undefined,
    rootSpeciality?: JobSearchRootSpeciality,
  ): Promise<JobSearchSubSpeciality | undefined> => {
    if (!newGrade || newGrade === 'none') {
      return undefined;
    }
    if (!rootSpeciality || rootSpeciality === 'none') {
      return undefined;
    }

    let subSpecialitiesPerRootSpeciality: Array<ISubSpeciality>;
    const result = await getSubSpecialitiesV2(newGrade, rootSpeciality);

    if ('error' in result) {
      // the error get reported by makeRequestAndNoticeErrors, and we don't show any notification to the user
      return undefined;
    } else {
      setSubSpecialities({
        ...subSpecialities,
        [rootSpeciality]: result.response.data,
      });
      subSpecialitiesPerRootSpeciality = result.response.data;
    }

    // reset selected speciality if it is not found in the allowed specialty values
    if (subSpecialitiesPerRootSpeciality && subSpecialitiesPerRootSpeciality.length > 0) {
      const subSpecialitiesSet: Set<JobSearchSubSpeciality | undefined> = new Set(
        subSpecialitiesPerRootSpeciality.map(i => i.value),
      );
      return subSpecialitiesSet.has(subspeciality) ? subspeciality : undefined;
    }

    return undefined;
  };

  // Fetch the list of grades and specialties on initial load
  useEffect(() => {
    const getJobGradesAndSpecialtiesV1 = async () => {
      try {
        const grades = await getJobGrades();
        setGrades(grades);
        const speciality = await getSpecialityByGrade(query?.grade);
        setSpeciality(speciality);
      } catch (error) {
        // error already reported by makeRequestAndNoticeErrors
        return null;
      }
    };

    const getJobGradesAndRootSpecialtiesAndSubSpecialitiesV2 = async () => {
      try {
        const grades = await getJobGrades();
        setGrades(grades);
        const rootSpeciality = await getRootSpecialityByGrade(query?.grade);
        setRootSpeciality(rootSpeciality);
        const subspeciality = await getSubSpecialityByRootSpeciality(query?.grade, rootSpeciality);
        setSubspeciality(subspeciality);
      } catch (error) {
        // error already reported by makeRequestAndNoticeErrors
        return null;
      }
    };

    if (showSubSpecialtyFormFilter) {
      getJobGradesAndRootSpecialtiesAndSubSpecialitiesV2();
    } else {
      getJobGradesAndSpecialtiesV1();
    }
  }, []);

  function handleChange(search: JobSearchWebCriteria) {
    router.push(generateJobListingsUrlObject(search));
  }

  function handleClearAll() {
    setRadius(undefined);
    setWorkType(undefined);
    setKeywords(undefined);
    setGrade(undefined);
    setSpeciality(undefined);
    setDate({ startDate: null, endDate: null });
    setLocationCoordinate(null);
    setMinPayRate(null);
    setMinPayRateUnit(DEFAULT_MIN_PAY_RATE_UNIT);
    track('Apply Job Search Filter', {
      applied_filter: {
        filter_type: 'clear',
        filter_value: 'all',
      },
      search: {},
    });
    handleChange({});
  }

  function getGradeOpts() {
    return getGradeOptions(grades);
  }

  function getSpecialityOpts() {
    return getSpecialityOptions(grade, specialities);
  }

  // Dependent on showSubSpecialtyFormFilter Feature flag
  function getRootSpecialityOpts() {
    return getRootSpecialityOptions(grade, rootSpecialities);
  }
  function getSubSpecialityOpts() {
    return getSubSpecialityOptions(rootSpeciality, subSpecialities);
  }

  function formatLocation(newLocationCoordinate: LocationCoordinateFilter): {
    location: string | undefined;
    locationName: string | undefined;
    lon: string | undefined;
    lat: string | undefined;
  } {
    return {
      location: (newLocationCoordinate as ISchedulerLocation)?.searchLocations?.join(';'),
      locationName: newLocationCoordinate?.name,
      lon: (newLocationCoordinate as ILocation)?.position?.lon.toString(),
      lat: (newLocationCoordinate as ILocation)?.position?.lat.toString(),
    };
  }

  function formatPayRate(newMinPayRate: MinPayRateFilter): {
    minPayRate: string | null;
    minPayRateUnit: MinPayRateUnit | undefined;
  } {
    return {
      minPayRate: newMinPayRate,
      minPayRateUnit: newMinPayRate ? minPayRateUnit : undefined,
    };
  }

  function generateSearchObject(changedProps: Partial<JobSearchWebCriteria>): JobSearchWebCriteria {
    return {
      ...date,
      grade,
      speciality,
      rootSpeciality,
      subspeciality,
      keywords,
      workType,
      ...formatPayRate(minPayRate),
      ...formatLocation(locationCoordinate),
      radius,
      sortBy: query?.sortBy,
      page: 1, // always set page back to 1 on filter change
      ...changedProps,
    };
  }

  function handleDateChange(newDate: DateFilter) {
    // only handle date changes when the date is valid to allow a possibility of typing dates in manually
    setDate(newDate);
    const search = generateSearchObject(newDate);
    if (
      date.startDate === newDate.startDate &&
      date.endDate !== newDate.endDate && // end date changed and
      isValidDate(newDate.endDate, 'YYYY-MM-DD') // and it is valid
    ) {
      track('Apply Job Search Filter', {
        applied_filter: {
          filter_type: 'endDate',
          filter_value: newDate.endDate,
        },
        search,
      });
      handleChange(search);
    }
    if (
      date.startDate !== newDate.startDate && // start date changed
      date.endDate === newDate.endDate &&
      isValidDate(newDate.startDate, 'YYYY-MM-DD') // and it is valid
    ) {
      track('Apply Job Search Filter', {
        applied_filter: {
          filter_type: 'startDate',
          filter_value: newDate.startDate,
        },
        search,
      });
      handleChange(search);
    }
  }

  async function handleGradeChange(newGrade: GradeFilter) {
    // side effects: specialty or rootSpecilty depending on showSubSpecialtyFormFilter Feature flag
    // - fetch new list if it is not fetched yet
    // - keep the selected specialty if on the new list or reset
    // TODO: refactor this to just change the rootSpeciality when ready
    let search: JobSearchWebCriteria;
    if (showSubSpecialtyFormFilter) {
      // Change the RootSpeciality
      const newRootSpeciality = await getRootSpecialityByGrade(newGrade);
      setRootSpeciality(newRootSpeciality);
      const newSubspeciality = await getSubSpecialityByRootSpeciality(newGrade, newRootSpeciality);
      setSubspeciality(newSubspeciality);
      search = generateSearchObject({
        grade: newGrade,
        rootSpeciality: newRootSpeciality,
        subspeciality: newSubspeciality,
      });
    } else {
      // Change the OG Speciality
      const newSpeciality = await getSpecialityByGrade(newGrade);
      setSpeciality(newSpeciality);
      search = generateSearchObject({ grade: newGrade, speciality: newSpeciality });
    }
    setGrade(newGrade);

    track('Apply Job Search Filter', {
      applied_filter: {
        filter_type: 'grade',
        filter_value: newGrade,
      },
      search,
    });
    if (newGrade) {
      setGradeCookie(newGrade);
    }
    handleChange(search);
  }

  async function handleRootSpecialityChange(newRootSpeciality: RootSpecialityFilter) {
    // side effects: subspecialty
    // - fetch new list if it is not fetched yet
    // - keep the selected subspecialty if on the new list or reset
    const newSubSpeciality = await getSubSpecialityByRootSpeciality(grade, newRootSpeciality);
    setRootSpeciality(newRootSpeciality);
    setSubspeciality(newSubSpeciality);
    const search = generateSearchObject({
      rootSpeciality: newRootSpeciality,
      subspeciality: newSubSpeciality,
    });
    track('Apply Job Search Filter', {
      applied_filter: {
        filter_type: 'rootSpeciality',
        filter_value: newRootSpeciality,
      },
      search,
    });
    handleChange(search);
  }

  function handleSpecialtyChange(newSpeciality: SpecialityFilter) {
    setSpeciality(newSpeciality);
    const search = generateSearchObject({ speciality: newSpeciality });
    track('Apply Job Search Filter', {
      applied_filter: {
        filter_type: 'speciality',
        filter_value: newSpeciality,
      },
      search,
    });
    if (newSpeciality) {
      setSpecialityCookie(newSpeciality);
    }
    handleChange(search);
  }

  function handleSubSpecialityChange(newSubSpeciality: SubSpecialityFilter) {
    setSubspeciality(newSubSpeciality);
    const search = generateSearchObject({ subspeciality: newSubSpeciality });
    track('Apply Job Search Filter', {
      applied_filter: {
        filter_type: 'subspeciality',
        filter_value: newSubSpeciality,
      },
      search,
    });
    handleChange(search);
  }

  function handleKeywordsChange(newKeywords: KeywordsFilter) {
    setKeywords(newKeywords);
    const search = generateSearchObject({ keywords: newKeywords });
    track('Apply Job Search Filter', {
      applied_filter: {
        filter_type: 'keywords',
        filter_value: newKeywords,
      },
      search,
    });

    handleChange(search);
  }

  function handleWorkTypeChange(newWorkType: WorkTypeFilter) {
    // side effects: end date
    // - reset end date if Permanent is selected
    setWorkType(newWorkType);
    let newEndDate = date.endDate;
    if (newWorkType === 'Permanent') {
      setDate({
        startDate: date.startDate,
        endDate: null,
      });
      newEndDate = null;
    }
    const search = generateSearchObject({ workType: newWorkType, endDate: newEndDate });
    track('Apply Job Search Filter', {
      applied_filter: {
        filter_type: 'workType',
        filter_value: newWorkType,
      },
      search,
    });
    handleChange(search);
  }

  function handleLocationChange(newLocationCoordinate: LocationCoordinateFilter) {
    // side effects: radius
    // - set to default or remove depending on type of location
    const newLocation = formatLocation(newLocationCoordinate);
    const newRadius = newLocation?.lat && newLocation?.lon ? radius ?? DEFAULT_RADIUS : undefined;
    setLocationCoordinate(newLocationCoordinate);
    setRadius(newRadius);
    const search = generateSearchObject({ ...newLocation, radius: newRadius });
    track('Apply Job Search Filter', {
      applied_filter: {
        filter_type: 'location',
        filter_value: newLocation,
      },
      search,
    });
    if (newLocationCoordinate) {
      setLocationCookie(newLocationCoordinate);
    }
    handleChange(search);
  }

  function handleRadiusChange(newRadius: RadiusFilter) {
    setRadius(newRadius);
    const search = generateSearchObject({ radius: newRadius });
    track('Apply Job Search Filter', {
      applied_filter: {
        filter_type: 'radius',
        filter_value: newRadius,
      },
      search,
    });
    handleChange(search);
  }

  function handleMinPayRateChange(newMinPayRate: MinPayRateFilter) {
    // side effects: minPayRateUnit
    // - set to default or remove
    setMinPayRate(newMinPayRate);
    const newPayRate = formatPayRate(newMinPayRate);
    const search = generateSearchObject(newPayRate);
    track('Apply Job Search Filter', {
      applied_filter: {
        filter_type: 'minPayRate',
        filter_value: newPayRate,
      },
      search,
    });
    handleChange(search);
  }

  return (
    <JobSearchFiltersForm
      date={date}
      onDateChange={handleDateChange}
      grade={grade}
      onGradeChange={grade => {
        handleGradeChange(grade)
          .then(() => null)
          .catch(() => null); // the error get reported by makeRequestAndNoticeErrors, and we don't show any notification to the user
      }}
      speciality={speciality}
      onSpecialityChange={handleSpecialtyChange}
      keyword={keywords}
      onKeywordChange={handleKeywordsChange}
      workType={workType}
      onWorkTypeChange={handleWorkTypeChange}
      locationCoordinate={locationCoordinate}
      onLocationCoordinateChange={handleLocationChange}
      radius={radius}
      onRadiusChange={handleRadiusChange}
      minPayRate={minPayRate}
      onMinPayRateChange={handleMinPayRateChange}
      minPayRateUnit={minPayRateUnit}
      onMinPayRateUnitChange={setMinPayRateUnit}
      isEndDateDisabled={isEndDateDisabled}
      isRadiusDisabled={isRadiusDisabled}
      onClear={handleClearAll}
      getGradeOptions={getGradeOpts}
      getSpecialityOptions={getSpecialityOpts}
      // TODO: remove speciality functions when the sub specialties are ready and we delete showSubSpecialtyFormFilter
      // The below are subject to showSubSpecialtyFormFilter Feature flag
      showSubSpecialtyFormFilter={showSubSpecialtyFormFilter}
      rootSpeciality={rootSpeciality}
      onRootSpecialityChange={rootSpeciality => {
        handleRootSpecialityChange(rootSpeciality)
          .then(() => null)
          .catch(() => null); // Same behaviour as grade now we have sub specialities
      }}
      subspeciality={subspeciality}
      onSubSpecialityChange={handleSubSpecialityChange}
      getRootSpecialityOptions={getRootSpecialityOpts}
      getSubSpecialityOptions={getSubSpecialityOpts}
    />
  );
};

export default JobSearchFilters;
